import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {RepublicaMovilService} from '../../services/republica-movil.service';
import * as moment from 'moment';
import {MatStepper} from '@angular/material/stepper';
import {environment} from '../../../../environments/environment';
import {cifValidator, dniValidator, getMaxLengthByDoc, nieValidator} from '../../../utils/validators/spanish-id-validator';
import {RmUserDataService} from '../../services/rm-user-data.service';
import {RmNormalizadorService} from '../../services/rm-normalizador.service';
import {MatDialog} from '@angular/material/dialog';
import {RmUserBankingDataComponent} from '../rm-user-banking-data/rm-user-banking-data.component';
import {RmOrderService} from '../../services/rm-order.service';
import {brands, maxDate, minDate, mobileContractType} from 'src/app/shared/constantes';
import {ibanValidator} from 'src/app/utils/validators/iban-validator';
import {Observable, of, Subscription} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {autocompleteValidator} from '../../../utils/validators/autocomplete-validator';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {onChangeZip, searchRoadType, SearchRoadTypeListener} from 'src/app/utils/normalizerFunctions';
import {MatSnackBar} from "@angular/material/snack-bar";

export interface DialogData {
    formGroup: FormGroup;
}

@Component({
    selector: 'app-rm-user-data',
    templateUrl: './rm-user-data.component.html',
    styleUrls: ['./rm-user-data.component.css', '../../../../assets/css/rm-theme.css'],
    standalone: false
})
export class RmUserDataComponent implements OnInit, SearchRoadTypeListener, OnDestroy {
    @Input() formGroup: FormGroup;
    @Input() stepper: MatStepper;

    private coverageData: any;

    public requireCif: boolean;
    public documentTypes = [];
    public countries = [];
    public provinces = [];
    public roadTypes = [];
    public fullNameBanking = '';
    public country = environment.googleMapsCountry;
    public maxDate: Date = maxDate;
    public minDate: Date = minDate;
    public showBankingData: boolean = true;
    public cities: [];
    public cityFilteredOptions: Observable<any[]>;
    public auxOrder;
    public maxLengthByDoc = 9;
    public requestSelectionRoadTypeManually = false;
    private allSubscription: Subscription[] = [];

    constructor(
        private matDialog: MatDialog,
        private rmService: RepublicaMovilService,
        private normalizadorService: RmNormalizadorService,
        private userDataService: RmUserDataService,
        private orderService: RmOrderService,
        private modalService: NgbModal,
        private snackBar: MatSnackBar
    ) { }

    ngOnInit(): void {
        this.allSubscription.push(this.rmService.getDocumentTypes()
            .subscribe((data: any) => {
                if (data && this.documentTypes && !this.documentTypes.length) {
                    this.documentTypes = data;
                }
            })
        );

        this.allSubscription.push(this.rmService.getCountries()
            .subscribe((data: any) => {
                if (data && this.countries && !this.countries.length) {
                    this.countries = data;
                }
            })
        );

        this.allSubscription.push(this.rmService.getProvinces()
            .subscribe((data: any) => {
                if (data && this.provinces && !this.provinces.length) {
                    this.provinces = data;
                    if (this.formGroup.get('codigoPostal').value != null && this.formGroup.get('codigoPostal').value !== '') {
                        this.onChangeZip(this.formGroup.get('codigoPostal').value);
                    }
                }
            })
        );

        this.allSubscription.push(this.rmService.getRoadTypes()
            .subscribe((data: any) => {
                if (data && this.roadTypes && !this.roadTypes.length) {
                    this.roadTypes = data;
                }
            })
        );

        this.allSubscription.push(this.normalizadorService.getResultadoCobertura()
            .subscribe((data: any) => {
                if (data != null && data.value.resultadoConsultaCobertura) {
                    this.coverageData = data;
                    this.setDataFromCoverage();
                }
            })
        );

        this.stepper.selectionChange
            .subscribe((data) => {
                if ((this.stepper.steps.length - 2) === data.previouslySelectedIndex && this.validate()) {
                    this.userDataService.setUserData(this.formGroup.getRawValue());
                }
                if (this.auxOrder && this.auxOrder.broadband) {
                    this.formGroup.get('localidad').markAsTouched();
                    this.formGroup.get('localidad').updateValueAndValidity();
                }
            });

        this.allSubscription.push(this.orderService.getOrder()
            .subscribe((data: any) => {
                if (data) {
                    this.auxOrder = data;
                    // Convergente o solo fibra
                    if (data.broadband) {
                        this.showBankingData = true;
                        // Solo móvil
                    } else if (data.mobiles && data.mobiles.length) {
                        const found = data.mobiles.find(mobile => mobile.contract_type === mobileContractType.postpaid || !mobile.contract_type);
                        this.showBankingData = found ? true : false;
                    }

                    if (this.showBankingData) {
                        this.formGroup.get('bank_account').setValidators([Validators.required, ibanValidator()]);
                    } else {
                        this.formGroup.get('bank_account').clearValidators();
                        this.formGroup.get('bank_account').setValidators([]);
                        this.formGroup.get('bank_account').updateValueAndValidity();
                        this.formGroup.get('bank_account').patchValue('');
                    }
                }
            })
        );

        if (this.formGroup.get('bank_account').value !== '') {
            this.fullNameBanking = this.formGroup.get('account_firstName').value + ' ' +
                this.formGroup.get('account_surname1').value + ' ' +
                this.formGroup.get('account_surname2').value;
        }

        if (this.formGroup.get('doctype').value != null && this.formGroup.get('doctype').value !== '') {
            this.changeTypeDocument(this.formGroup.get('doctype').value);
        }

        if (this.formGroup.get('codigoPostal').value != null && this.formGroup.get('codigoPostal').value !== '') {
            this.onChangeZip(this.formGroup.get('codigoPostal').value);
        }

        this.formGroup.get('provincia').disable();
    }

    validate(): boolean {
        this.setBankingData();
        this.formGroup.markAllAsTouched();
        this.formGroup.updateValueAndValidity();

        if (!this.formGroup.get('tipoVia').value || this.formGroup.get('tipoVia').value === '') {
            searchRoadType(this.formGroup.get('nombreVia').value, this.roadTypes, 'code', this.formGroup.get('tipoVia'), this);
        }

        if (!this.formGroup.valid) {
            const elements: any = document.getElementsByClassName('mat-input-element ng-invalid');

            if (elements.length > 0) {
                elements[0].focus();
            }

            return false;
        }

        return true;
    }

    next() {
        if (this.validate()) {
            this.userDataService.setUserData(this.formGroup.getRawValue());
            this.stepper.next();
        }
    }

    prev() {
        this.stepper.previous();
    }

    changeTypeDocument(documentTypeId): void {
        const docValidators: ValidatorFn[] = [Validators.required];

        if (typeof documentTypeId === 'number') {
            documentTypeId = documentTypeId.toString();
        }

        this.requireCif = documentTypeId === '4';

        if (this.requireCif) {
            this.formGroup.get('company').setValidators(Validators.required);
        } else {
            this.formGroup.get('company').clearValidators();
        }

        switch (documentTypeId) {
            case '1':
                docValidators.push(dniValidator());
                break;
            case '2':
                if (this.formGroup.get('country').value === 'ES') {
                    this.formGroup.get('country').patchValue(null);
                }
                docValidators.push(nieValidator());
                break;
            case '4':
                docValidators.push(cifValidator());
                break;
            default:
        }

        this.formGroup.get('doc').setValidators(docValidators);
        this.formGroup.get('doc').updateValueAndValidity();
        this.formGroup.get('company').updateValueAndValidity();
    }

    setMaxLengthByDoc(type) {
        this.maxLengthByDoc = getMaxLengthByDoc(brands.rm, type);
    }

    onChangeZip(value) {
        if (value && value.toString().length > 3 && this.formGroup.get('localidad').value && this.formGroup.get('localidad').value !== '') {
            this.searchCity(this.formGroup.get('localidad').value);
        }
        this.cityFilteredOptions = of([])
        onChangeZip(value, this.provinces, 'postal_code', this.formGroup.get('provincia'));
    }

    onAutocompleteSelected($event) {
        const addressComponents: any[] = $event.address_components;

        if (addressComponents != null && addressComponents.length > 0) {
            this.formGroup.get('tipoVia').patchValue('');
            this.formGroup.get('nombreVia').patchValue('');
            this.formGroup.get('numero').patchValue('');
            this.formGroup.get('planta').patchValue('');
            this.formGroup.get('puerta').patchValue('');
            this.formGroup.get('escalera').patchValue('');
            this.formGroup.get('codigoPostal').patchValue('');
            this.formGroup.get('localidad').patchValue('');
            this.formGroup.get('provincia').patchValue('');

            for (const addressComponent of addressComponents) {
                let field = '';
                const types: string[] = addressComponent.types;
                let value: string = addressComponent.long_name;

                if (types.includes('route')) {
                    field = 'nombreVia';
                    value = searchRoadType(value, this.roadTypes, 'code', this.formGroup.get('tipoVia'), this);
                } else if (types.includes('street_number')) {
                    field = 'numero';
                } else if (types.includes('postal_code')) {
                    field = 'codigoPostal';
                    this.onChangeZip(value);
                } else if (types.includes('locality')) {
                    field = 'localidad';
                    this.searchCity(value, true);
                } else if (types.includes('administrative_area_level_2')) {
                    field = 'provincia';
                    value = value.toUpperCase();
                }

                if (field !== '') {
                    this.formGroup.get(field).patchValue(value);
                }
            }
        }
    }

    setDataFromCoverage() {
        if (this.coverageData) {
            if (this.coverageData.get('tipoViaCobertura').value && (this.formGroup.get('tipoVia').value || this.formGroup.get('tipoVia').value === '')) {
                searchRoadType(this.coverageData.get('tipoViaCobertura').value, this.roadTypes, 'code', this.formGroup.get('tipoVia'), this);
            }
            if (this.coverageData.get('nombreViaCobertura').value && (this.formGroup.get('nombreVia').value || this.formGroup.get('nombreVia').value === '')) {
                this.formGroup.get('nombreVia').patchValue(this.coverageData.get('tipoViaCobertura').value && !this.requestSelectionRoadTypeManually ?
                    this.coverageData.get('tipoViaCobertura').value + ' ' + this.coverageData.get('nombreViaCobertura').value :
                    this.coverageData.get('nombreViaCobertura').value);
            }
            if (this.coverageData.get('numeroCobertura').value && (this.formGroup.get('numero').value || this.formGroup.get('numero').value === '')) {
                this.formGroup.get('numero').patchValue(this.coverageData.get('numeroCobertura').value);
            }
            if (this.coverageData.get('provinciaCobertura').value && (this.formGroup.get('provincia').value || this.formGroup.get('provincia').value === '')) {
                this.formGroup.get('provincia').patchValue(this.coverageData.get('provinciaCobertura').value);
            }
            if (this.coverageData.get('plantaCobertura').value && (this.formGroup.get('planta').value || this.formGroup.get('planta').value === '')) {
                this.formGroup.get('planta').patchValue(this.coverageData.get('plantaCobertura').value);
            }
            if (this.coverageData.get('puertaCobertura').value && (this.formGroup.get('puerta').value || this.formGroup.get('puerta').value === '')) {
                this.formGroup.get('puerta').patchValue(this.coverageData.get('puertaCobertura').value);
            }
            if (this.coverageData.get('escaleraCobertura').value && (this.formGroup.get('escalera').value || this.formGroup.get('escalera').value === '')) {
                this.formGroup.get('escalera').patchValue(this.coverageData.get('escaleraCobertura').value);
            }
            if (this.coverageData.get('codigoPostalCobertura').value && (this.formGroup.get('codigoPostal').value || this.formGroup.get('codigoPostal').value === '')) {
                this.formGroup.get('codigoPostal').patchValue(this.coverageData.get('codigoPostalCobertura').value);
                this.onChangeZip(this.coverageData.get('codigoPostalCobertura').value);
            }
        }
    }

    onIbanChange(event) {
        this.formGroup.get('bank_account').patchValue(event.target.value);
        this.setBankingData();
    }

    openModal() {
        this.setBankingData();

        const dialogRef = this.matDialog.open(RmUserBankingDataComponent, {
            maxWidth: '798px',
            data: {
                formGroup: this.formGroup
            },
            disableClose: true,
            panelClass: 'no-padding'
        });

        dialogRef.afterClosed().subscribe((result: DialogData) => {
            if (result != null) {
                this.formGroup.patchValue(result.formGroup.getRawValue());
                this.fullNameBanking = this.formGroup.get('account_firstName').value + ' ' +
                    this.formGroup.get('account_surname1').value + ' ' +
                    this.formGroup.get('account_surname2').value;
            }
        });
    }

    setBankingData() {
        const userData = this.formGroup.getRawValue();

        if (this.formGroup.get('doctype').valid && (userData.account_doctype == null || userData.account_doctype === '')) {
            this.formGroup.get('account_doctype').patchValue(userData.doctype);
        }

        if (this.formGroup.get('doc').valid && (userData.account_doc == null || userData.account_doc === '')) {
            this.formGroup.get('account_doc').patchValue(userData.doc);
        }

        if (userData.account_country == null || userData.account_country === '') {
            this.formGroup.get('account_country').patchValue(userData.country);
        }

        if (userData.account_firstName == null || userData.account_firstName === '') {
            this.formGroup.get('account_firstName').patchValue(userData.firstName);
        }

        if (userData.account_surname1 == null || userData.account_surname1 === '') {
            this.formGroup.get('account_surname1').patchValue(userData.surname1);
        }

        if (userData.account_surname2 == null || userData.account_surname2 === '') {
            this.formGroup.get('account_surname2').patchValue(userData.surname2);
        }

        if (userData.account_company == null || userData.account_company === '') {
            this.formGroup.get('account_company').patchValue(userData.company);
        }

        this.fullNameBanking = this.formGroup.get('account_firstName').value + ' ' +
            this.formGroup.get('account_surname1').value + ' ' +
            this.formGroup.get('account_surname2').value;
    }

    searchCity(value, clean?) {
        this.formGroup.get('localidad').clearValidators();

        if (value.length >= 3) {
            this.rmService.getCitiesProvidersFromService(value)
                .subscribe((data: any) => {
                    if (this.formGroup.get('codigoPostal').value && this.formGroup.get('codigoPostal').value !== '') {
                        this.cities = data.msg.filter(option => option.province.postCode === ('00000' + this.formGroup.get('codigoPostal').value.toString()).slice(-5).substring(0, 2));
                    } else {
                        this.cities = data.msg;
                    }
                    this.cityFilteredOptions = this.formGroup.get('localidad').valueChanges.pipe(
                        startWith(''),
                        map(val => this._filter(val, this.cities))
                    );

                    this.formGroup.get('localidad').setValidators([
                        Validators.required,
                        autocompleteValidator(this.cities.map((i: any) => i.normalized))
                    ]);
                    this.formGroup.get('localidad').markAsTouched();
                    this.formGroup.get('localidad').updateValueAndValidity();
                    if (clean && !this.formGroup.get('localidad').valid) {
                        this.formGroup.get('localidad').patchValue('');
                    }
                });
        } else {
            this.formGroup.get('localidad').setValidators(Validators.required);
            this.formGroup.get('localidad').updateValueAndValidity();
        }
    }

    openModalPioneros(content) {
        this.modalService.open(content, {size: 'lg', centered: true});
    }

    private _filter(value: string, values: any[]): any[] {
        const filterValue = value.toLowerCase();

        const auxValues = values.filter(option => option.name.toLowerCase().includes(filterValue));
        return auxValues && auxValues.length === 0 ? values : auxValues;
    }

    onZeroResults() {
        this.requestSelectionRoadTypeManually = true;
        this.formGroup.get('tipoVia').markAsTouched();
        this.formGroup.get('tipoVia').updateValueAndValidity();
    }

    ngOnDestroy() {
        if (this.allSubscription && this.allSubscription.length) {
            this.allSubscription.map(subs => subs.unsubscribe());
        }
    }
}
